/**
 * Wishlist
 * --------
 * Lightweight, storage-based wishlist for the Impact theme.
 * - Persists product handles in localStorage (per browser/device).
 * - Powers the heart button on product cards (snippets/product-card.liquid).
 * - Powers the wishlist page (sections/main-wishlist.liquid).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'shamarka:wishlist';

  function getWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      // Storage unavailable (e.g. private browsing) - fail silently
    }
  }

  function isWishlisted(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function toggleWishlist(handle) {
    var handles = getWishlist();
    var index = handles.indexOf(handle);
    var added;

    if (index === -1) {
      handles.push(handle);
      added = true;
    } else {
      handles.splice(index, 1);
      added = false;
    }

    saveWishlist(handles);

    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { handle: handle, added: added, count: handles.length }
      })
    );

    return added;
  }

  function updateHeartButtons(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-wishlist-toggle]');

    buttons.forEach(function (button) {
      var handle = button.getAttribute('data-handle');
      var active = isWishlisted(handle);

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function updateWishlistCounts() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('opacity-0', count === 0);
    });
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.themeVariables && window.themeVariables.settings.moneyFormat) || '${{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number === null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (true) {
      case placeholderRegex.test(formatString) && /amount_no_decimals/.test(formatString):
        value = formatWithDelimiters(cents, 0);
        break;
      case /amount_with_comma_separator/.test(formatString):
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case /amount_no_decimals_with_comma_separator/.test(formatString):
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function renderCard(product, moneyFormat) {
    var image = product.featured_image || (product.images && product.images[0]) || '';
    if (image && image.indexOf('?') === -1) image += '?width=600';

    var priceHtml = '<span class="sale-price text-subdued">' + formatMoney(product.price, moneyFormat) + '</span>';
    if (product.compare_at_price && product.compare_at_price > product.price) {
      priceHtml =
        '<span class="sale-price text-on-sale">' + formatMoney(product.price, moneyFormat) + '</span>' +
        '<span class="compare-at-price text-subdued" style="text-decoration:line-through;margin-inline-start:.5em;">' +
        formatMoney(product.compare_at_price, moneyFormat) +
        '</span>';
    }

    var li = document.createElement('li');
    li.className = 'product-card wishlist-card';
    li.innerHTML =
      '<div class="product-card__figure">' +
      '<a href="' + product.url + '" data-instant>' +
      '<img class="product-card__image product-card__image--primary" src="' + image + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="600" height="600">' +
      '</a>' +
      '<button type="button" class="wishlist-heart-btn is-active" data-wishlist-toggle data-handle="' + product.handle + '" aria-pressed="true" aria-label="Remove from wishlist">' +
      wishlistHeartSvg() +
      '</button>' +
      '</div>' +
      '<div class="product-card__info">' +
      '<div class="v-stack gap-0.5 w-full">' +
      '<span class="product-card__title"><a href="' + product.url + '" class="bold" data-instant>' + escapeHtml(product.title) + '</a></span>' +
      '<price-list class="price-list">' + priceHtml + '</price-list>' +
      '</div>' +
      '</div>';

    return li;
  }

  function wishlistHeartSvg() {
    return (
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M12 21s-7.5-4.6-10-9.1C.4 8.8 1.7 5 5.3 4.1 8 3.4 10.4 5 12 7.2 13.6 5 16 3.4 18.7 4.1 22.3 5 23.6 8.8 22 11.9 19.5 16.4 12 21 12 21Z"/>' +
      '</svg>'
    );
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function renderWishlistPage() {
    var container = document.getElementById('wishlist-page');
    if (!container) return;

    var grid = container.querySelector('.wishlist-grid');
    var emptyState = container.querySelector('.wishlist-empty');
    var loadingState = container.querySelector('.wishlist-loading');
    var moneyFormat = container.getAttribute('data-money-format') || null;
    var handles = getWishlist();

    if (!grid) return;

    if (handles.length === 0) {
      grid.hidden = true;
      if (loadingState) loadingState.hidden = true;
      if (emptyState) emptyState.hidden = false;
      return;
    }

    if (emptyState) emptyState.hidden = true;
    if (loadingState) loadingState.hidden = false;
    grid.hidden = true;
    grid.innerHTML = '';

    var validHandles = [];
    var fetches = handles.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (response) {
          if (!response.ok) throw new Error('not found');
          return response.json();
        })
        .then(function (product) {
          validHandles.push(handle);
          return product;
        })
        .catch(function () {
          return null;
        });
    });

    Promise.all(fetches).then(function (products) {
      products = products.filter(Boolean);

      if (validHandles.length !== handles.length) {
        saveWishlist(validHandles);
        updateWishlistCounts();
      }

      if (loadingState) loadingState.hidden = true;

      if (products.length === 0) {
        if (emptyState) emptyState.hidden = false;
        grid.hidden = true;
        return;
      }

      products.forEach(function (product) {
        grid.appendChild(renderCard(product, moneyFormat));
      });

      grid.hidden = false;
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-handle');
    if (!handle) return;

    toggleWishlist(handle);
    updateHeartButtons(document);
    updateWishlistCounts();

    if (document.getElementById('wishlist-page')) {
      renderWishlistPage();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    updateHeartButtons(document);
    updateWishlistCounts();
    renderWishlistPage();
  });

  document.addEventListener('shopify:section:load', function () {
    updateHeartButtons(document);
  });
})();
